import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { toast } from "sonner";
import { useAuthStore } from "./lib/store";
import { authAPI, setLoginTime } from "./lib/api";
import { prefetchAllData } from "./lib/prefetch";
import { AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import TrendsPage from "./pages/TrendsPage";
import CreateVideoPage from "./pages/CreateVideoPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import VideoLibraryPage from "./pages/VideoLibraryPage";
import SettingsPage from "./pages/SettingsPage";
import EngagementPage from "./pages/EngagementPage";
import ViralPredictorPage from "./pages/ViralPredictorPage";
import TemplatesPage from "./pages/TemplatesPage";
import ContentCalendarPage from "./pages/ContentCalendarPage";
import SoundsPage from "./pages/SoundsPage";
import ThumbnailGeneratorPage from "./pages/ThumbnailGeneratorPage";
import ImageStudioPage from "./pages/ImageStudioPage";
import TikTokConnectionPage from "./pages/TikTokConnectionPage";
import CrossPostPage from "./pages/CrossPostPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import UGCStudioPage from "./pages/UGCStudioPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import CreditSuccessPage from "./pages/CreditSuccessPage";
import AdminTrialManagement from "./pages/AdminTrialManagement";
import ReferralDashboard from "./pages/ReferralDashboard";
import ScriptDoctorPage from "./pages/ScriptDoctorPage";
import BrandKitPage from "./pages/BrandKitPage";
import ViralMakerPage from "./pages/ViralMakerPage";
import AccountManagerAdminPage from "./pages/AccountManagerAdminPage";
import StaffManagementPage from "./pages/StaffManagementPage";
import MyClientsPage from "./pages/MyClientsPage";
import VideoStudioPage from "./pages/VideoStudioPage";
import PreLaunchChecklistPage from "./pages/PreLaunchChecklistPage";
import MediaLibraryPage from "./pages/MediaLibraryPage";
import SupportPage from "./pages/SupportPage";

// Components
import CookieConsentBanner from "./components/CookieConsentBanner";
import IGoAssist, { IGoAssistTrigger } from "./components/IGoAssist";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";

// Referral Code Persistence - captures ref code from URL and saves to localStorage
const ReferralCodePersistence = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Check query params first (?ref=CODE)
    const params = new URLSearchParams(location.search);
    let refCode = params.get('ref');
    
    // Also check path-based referral (/ref/CODE)
    if (!refCode && location.pathname.startsWith('/ref/')) {
      refCode = location.pathname.split('/ref/')[1];
      console.log('[REFERRAL] Captured referral code from path:', refCode);
    }
    
    if (refCode) {
      // Save referral code with timestamp (expires in 30 days)
      const referralData = {
        code: refCode.toUpperCase(),
        capturedAt: Date.now(),
        expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
        source: location.pathname + location.search // Track where it came from
      };
      localStorage.setItem('pending_referral', JSON.stringify(referralData));
      console.log('[REFERRAL] Captured and saved referral code:', refCode.toUpperCase(), 'from:', referralData.source);
      
      // Also set in sessionStorage as backup (survives page refreshes within session)
      sessionStorage.setItem('referral_code', refCode.toUpperCase());
    }
  }, [location.search, location.pathname]);
  
  return null;
};

// OAuth Callback Handler Component
const OAuthCallbackHandler = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const setAuth = useAuthStore((state) => state.setAuth);
  const [processing, setProcessing] = useState(false);

  const processOAuthCallback = useCallback(async () => {
    // Check for session_id in hash
    const hash = window.location.hash;
    if (!hash || !hash.includes("session_id")) return;

    setProcessing(true);
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setProcessing(false);
      return;
    }

    try {
      console.log("Processing OAuth callback with session:", sessionId.substring(0, 10) + "...");
      
      // Check for persisted referral code
      let referralCode = null;
      try {
        const savedReferral = localStorage.getItem('pending_referral');
        if (savedReferral) {
          const referralData = JSON.parse(savedReferral);
          if (referralData.expiresAt > Date.now()) {
            referralCode = referralData.code;
            console.log('[REFERRAL] Found persisted referral code for OAuth:', referralCode);
          }
        }
      } catch (e) {
        console.warn('[REFERRAL] Error reading referral from localStorage:', e);
      }
      
      // Exchange session_id for user session (with optional referral code)
      const response = await authAPI.googleSession(sessionId, referralCode);
      const { user, access_token, referral_bonus } = response.data;

      console.log("OAuth response received, access_token present:", !!access_token);

      // Mark login time to prevent 401 race condition logout
      setLoginTime();

      // CRITICAL: Always require access_token for mobile compatibility
      if (access_token) {
        setAuth(user, access_token);
        console.log("Auth set successfully with token");
      } else {
        // Fallback - but this should not happen
        console.warn("No access_token in response, using setUser fallback");
        setUser(user);
      }
      
      // Clear persisted referral code after successful OAuth
      localStorage.removeItem('pending_referral');
      
      if (referral_bonus) {
        toast.success(`Welcome, ${user.name}! You've been referred by ${referral_bonus.referrer_name}. Claim your ${referral_bonus.bonus_credits} free credits!`);
      } else {
        toast.success(`Welcome, ${user.name}!`);
      }

      // Start prefetching trends and sounds in the background
      prefetchAllData().catch(err => console.warn('[OAuth] Prefetch failed:', err));

      // Clear the hash BEFORE navigation
      window.history.replaceState(null, "", location.pathname);
      
      // Wait for state to be fully persisted before navigation
      // This prevents race conditions where API calls happen before token is saved
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Verify token is available
      const storedToken = localStorage.getItem('auth_token');
      console.log("Token verification before navigate:", storedToken ? "present" : "missing");
      
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("OAuth callback error:", error);
      toast.error(error.response?.data?.detail || "Authentication failed");
      navigate("/login", { replace: true });
    } finally {
      setProcessing(false);
    }
  }, [location.pathname, navigate, setAuth, setUser]);

  useEffect(() => {
    processOAuthCallback();
  }, [processOAuthCallback]);

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">Signing you in...</p>
          <p className="text-muted-foreground text-sm mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);
  
  // Debug logging
  console.log('[ProtectedRoute] Render - isAuthenticated:', isAuthenticated, 'token:', token ? 'present' : 'null', 'path:', location.pathname);
  
  // Wait for store hydration from localStorage
  useEffect(() => {
    // Check if there's a token in localStorage that hasn't been hydrated yet
    const storedToken = localStorage.getItem('auth_token');
    const storeToken = useAuthStore.getState().token;
    
    console.log('[ProtectedRoute] useEffect - storedToken:', storedToken ? 'present' : 'null', 'storeToken:', storeToken ? 'present' : 'null');
    
    // If there's a stored token but store doesn't have it, wait a bit for hydration
    if (storedToken && !storeToken) {
      console.log('[ProtectedRoute] Waiting for hydration...');
      const timeout = setTimeout(() => {
        console.log('[ProtectedRoute] Hydration timeout complete');
        setIsHydrated(true);
      }, 100);
      return () => clearTimeout(timeout);
    }
    setIsHydrated(true);
  }, []);
  
  // Allow through if there's a session_id in the hash (OAuth callback)
  if (location.hash && location.hash.includes("session_id")) {
    console.log('[ProtectedRoute] Allowing OAuth callback');
    return children;
  }
  
  // Show loading while hydrating
  if (!isHydrated) {
    console.log('[ProtectedRoute] Showing loading spinner (not hydrated)');
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
      </div>
    );
  }
  
  // Check for token in localStorage as fallback (handles race conditions)
  const hasStoredToken = localStorage.getItem('auth_token');
  
  console.log('[ProtectedRoute] Final check - isAuthenticated:', isAuthenticated, 'hasStoredToken:', hasStoredToken ? 'present' : 'null');
  
  if (!isAuthenticated && !hasStoredToken) {
    console.log('[ProtectedRoute] REDIRECTING TO LOGIN - no auth');
    return <Navigate to="/login" replace />;
  }
  
  console.log('[ProtectedRoute] Allowing access');
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Referral Redirect Component
const ReferralRedirect = () => {
  const { referralCode } = useParams();
  return <Navigate to={`/register?ref=${referralCode}`} replace />;
};

function App() {
  const [showIGoAssist, setShowIGoAssist] = useState(false);
  
  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <AppErrorBoundary>
      <BrowserRouter>
        <ReferralCodePersistence />
        <OAuthCallbackHandler>
          <AnimatePresence mode="wait">
            <Routes>
              {/* Landing Page - Public */}
              <Route path="/" element={<LandingPage />} />

              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />

              {/* Legal Pages - Public */}
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              
              {/* Pricing Page - Public but also accessible when logged in */}
              <Route path="/pricing" element={<PricingPage />} />
              
              {/* Subscription Success - Protected */}
              <Route 
                path="/subscription/success" 
                element={
                  <ProtectedRoute>
                    <SubscriptionSuccessPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Credit Purchase Success - Protected */}
              <Route 
                path="/credits/success" 
                element={
                  <ProtectedRoute>
                    <CreditSuccessPage />
                  </ProtectedRoute>
                } 
              />

              {/* Protected Routes - Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
              </Route>

              {/* Protected Routes - Other Pages */}
              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="trends" element={<TrendsPage />} />
                <Route path="create" element={<CreateVideoPage />} />
                <Route path="review" element={<ReviewQueuePage />} />
                <Route path="library" element={<Navigate to="/media-library?type=video" replace />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="engagement" element={<EngagementPage />} />
                <Route path="viral-predictor" element={<ViralPredictorPage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="calendar" element={<ContentCalendarPage />} />
                <Route path="sounds" element={<SoundsPage />} />
                <Route path="thumbnails" element={<ThumbnailGeneratorPage />} />
                <Route path="image-studio" element={<ImageStudioPage />} />
                <Route path="video-studio" element={<VideoStudioPage />} />
                <Route path="tiktok" element={<TikTokConnectionPage />} />
                <Route path="cross-post" element={<CrossPostPage />} />
                <Route path="admin" element={<AdminDashboardPage />} />
                <Route path="admin/trials" element={<AdminTrialManagement />} />
                <Route path="admin/account-managers" element={<AccountManagerAdminPage />} />
                <Route path="admin/staff" element={<StaffManagementPage />} />
                <Route path="admin/pre-launch" element={<PreLaunchChecklistPage />} />
                <Route path="ugc-studio" element={<UGCStudioPage />} />
                <Route path="referrals" element={<ReferralDashboard />} />
                <Route path="script-doctor" element={<ScriptDoctorPage />} />
                <Route path="brand-kit" element={<BrandKitPage />} />
                <Route path="viral-maker" element={<ViralMakerPage />} />
                <Route path="my-clients" element={<MyClientsPage />} />
                <Route path="media-library" element={<MediaLibraryPage />} />
                <Route path="support" element={<SupportPage />} />
              </Route>

              {/* Public Referral Signup Route */}
              <Route 
                path="/ref/:referralCode" 
                element={<ReferralRedirect />}
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AnimatePresence>
        </OAuthCallbackHandler>
      </BrowserRouter>
      </AppErrorBoundary>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#18181B',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff',
          },
        }}
      />
      <CookieConsentBanner />
      
      {/* iGoAssist - AI Helper */}
      <IGoAssistTrigger onClick={() => setShowIGoAssist(true)} />
      <IGoAssist 
        isOpen={showIGoAssist} 
        onClose={() => setShowIGoAssist(false)}
        currentPage={window.location.pathname}
      />
    </div>
  );
}

export default App;
